// src/components/viewer/ViewerTable.jsx

import React, { useState, useMemo } from 'react';

const ViewerTable = ({ tableData = [], requestId, riskType }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentFilter, setCurrentFilter] = useState('all');

    // Determine which columns to show based on risk type
    const rt = (riskType || 'CRP').toUpperCase();
    const showActivity = rt === 'HRP' || rt === 'CRP';
    const showJobFamily = rt === 'CRP';
    const showJobLevel = rt === 'CRP';
    const showSearchActivity = rt === 'CRP';
    const showUFSStatus = rt === 'CRP';

    // Filter data based on search and filter
    const filteredData = useMemo(() => {
        return tableData.filter(row => {
            let passesFilter = true;
            if (currentFilter === 'eligible') {
                passesFilter = row.eligible && row.eligible.includes('Eligible') && !row.eligible.includes('Not');
            } else if (currentFilter === 'ineligible') {
                passesFilter = row.eligible && row.eligible.includes('Not Eligible');
            } else if (currentFilter === 'notfound') {
                passesFilter = row.eligible && row.eligible.includes('Not Found');
            }

            const passesSearch = !searchTerm || 
                (row.login && row.login.toLowerCase().includes(searchTerm.toLowerCase()));

            return passesFilter && passesSearch;
        });
    }, [tableData, searchTerm, currentFilter]);

    // Get CSS classes for table cells
    const getActivityClass = (inactivity) => {
        if (inactivity === 'Active') return 'status-active';
        if (inactivity === 'Inactive') return 'status-inactive';
        return '';
    };

    const getBasePIClass = (basePIAccess) => {
        if (basePIAccess === 'Yes') return 'status-yes';
        if (basePIAccess === 'No') return 'status-no';
        return '';
    };

    const getJobLevelClass = (jobLevelStatus) => {
        if (jobLevelStatus) {
            if (jobLevelStatus.includes('can be granted') && !jobLevelStatus.includes('cannot')) {
                return 'status-yes';
            } else if (jobLevelStatus.includes('cannot')) {
                return 'status-no';
            }
        }
        return '';
    };

    const getJobLevelNumberClass = (jobLevel) => {
        const jobLevelNum = parseInt(jobLevel);
        if (!isNaN(jobLevelNum)) {
            return jobLevelNum >= 3 ? 'status-yes' : 'status-no';
        }
        return '';
    };

    const getEligibleClass = (eligible) => {
        if (eligible && eligible.includes('Eligible') && !eligible.includes('Not')) return 'status-eligible';
        if (eligible && eligible.includes('Not Eligible')) return 'status-not-eligible';
        return 'status-not-found';
    };

    const getRowClass = (eligible) => {
        if (eligible && eligible.includes('Eligible') && !eligible.includes('Not')) return 'eligible-row';
        if (eligible && eligible.includes('Not Eligible')) return 'ineligible-row';
        if (eligible && eligible.includes('Not Found')) return 'notfound-row';
        return '';
    };

    // 🆕 Search Activity badge renderer
    const renderSearchActivity = (status) => {
        switch (status) {
            case 'PASS':
                return (
                    <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: '#dcfce7',
                        color: '#166534',
                        border: '1px solid #bbf7d0'
                    }}>
                        ✅ Sufficient
                    </span>
                );
            case 'BELOW_THRESHOLD':
                return (
                    <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        border: '1px solid #fde68a'
                    }}>
                        ⚠️ Low Activity
                    </span>
                );
            case 'NO_DATA':
            default:
                return (
                    <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: '#f3f4f6',
                        color: '#6b7280',
                        border: '1px solid #e5e7eb'
                    }}>
                        — No Data
                    </span>
                );
        }
    };

    // 🆕 Updated CSV export — removed Days, added Search Activity
    const exportToCSV = () => {
        let csv = 'Login,Activity,Base PI Access,Job Family,Job Level,Search Activity,UFS Status,Eligible\n';

        filteredData.forEach(row => {
            csv += `"${row.login || ''}",`;
            csv += `"${row.inactivity || ''}",`;
            csv += `"${row.basePIAccess || ''}",`;
            csv += `"${row.jobFamily || ''}",`;
            csv += `"${row.jobLevel !== undefined ? row.jobLevel : ''}",`;
            csv += `"${row.searchActivity || 'NO_DATA'}",`;
            csv += `"${row.jobLevelStatus || ''}",`;
            csv += `"${(row.eligible || '').replace(/[✅❌⚠️]/g, '')}"\n`;
        });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `validation_results_${requestId || 'export'}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFilterClick = (filter) => {
        setCurrentFilter(filter);
    };

    return (
        <div className="table-section">
            <div className="section-title">📋 Detailed Results</div>
            
            <div className="table-controls">
                <input
                    type="text"
                    className="search-box"
                    placeholder="🔍 Search by login..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                
                <div className="filter-buttons">
                    <button
                        className={`filter-btn ${currentFilter === 'all' ? 'active' : ''}`}
                        onClick={() => handleFilterClick('all')}
                    >
                        All
                    </button>
                    <button
                        className={`filter-btn eligible ${currentFilter === 'eligible' ? 'active' : ''}`}
                        onClick={() => handleFilterClick('eligible')}
                    >
                        ✅ Eligible
                    </button>
                    <button
                        className={`filter-btn ineligible ${currentFilter === 'ineligible' ? 'active' : ''}`}
                        onClick={() => handleFilterClick('ineligible')}
                    >
                        ❌ Not Eligible
                    </button>
                    <button
                        className={`filter-btn notfound ${currentFilter === 'notfound' ? 'active' : ''}`}
                        onClick={() => handleFilterClick('notfound')}
                    >
                        ⚠️ Not Found
                    </button>
                </div>
                
                <button className="export-btn" onClick={exportToCSV}>
                    📥 Export CSV
                </button>
            </div>
            
            <div className="row-count">Showing {filteredData.length} users</div>
            
            <div className="table-container">
                <div className="table-scroll">
                    <table className="results-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Login</th>
                                {showActivity && <th>Activity</th>}
                                <th>Base PI</th>
                                {showJobFamily && <th>Job Family</th>}
                                {showJobLevel && <th>Job Level</th>}
                                {showSearchActivity && <th>Search Activity</th>}
                                {showUFSStatus && <th>UFS Status</th>}
                                <th>Eligible</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((row, index) => (
                                <tr key={index} className={getRowClass(row.eligible)}>
                                    <td>{index + 1}</td>
                                    <td><strong>{row.login || 'N/A'}</strong></td>
                                    {showActivity && (
                                        <td className={getActivityClass(row.inactivity)}>
                                            {row.inactivity || 'N/A'}
                                        </td>
                                    )}
                                    <td className={getBasePIClass(row.basePIAccess)}>
                                        {row.basePIAccess || 'N/A'}
                                    </td>
                                    {showJobFamily && <td>{row.jobFamily || 'N/A'}</td>}
                                    {showJobLevel && (
                                        <td className={getJobLevelNumberClass(row.jobLevel)}>
                                            {row.jobLevel !== undefined ? row.jobLevel : 'N/A'}
                                        </td>
                                    )}
                                    {showSearchActivity && <td>{renderSearchActivity(row.searchActivity)}</td>}
                                    {showUFSStatus && (
                                        <td className={getJobLevelClass(row.jobLevelStatus)}>
                                            {row.jobLevelStatus || 'N/A'}
                                        </td>
                                    )}
                                    <td>
                                        <span className={getEligibleClass(row.eligible)}>
                                            {row.eligible || 'N/A'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ViewerTable;